import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../Common_Files/Config';
import axios from 'axios';

/*
 * Purchase History Invoice PDF Generator
 * 
 * Changes made according to requirements:
 * 1. Ref field now displays Job Ref value instead of reference_no
 * 2. Date label changed to 'Date of Creation' showing PO creation date and time
 * 3. Arrival Date field renamed to 'GRN Completion Date' showing GRN completion date
 * 4. Delivery Address now includes Warehouse name and code
 * 5. Added view vs download mode functionality
 * 6. Fixed Job Ref text wrapping to prevent overlapping
 */
const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const generatePurchaseInvoicePDF = async (id, viewMode = false, idType = 'grn') => {
  const accessToken = localStorage.getItem('accessToken');

  const formData = new FormData();
  formData.set('id', id);
  const apiUrl = idType === 'purchase'
    ? `${config.api_url}/ws-get-purchase-history-view`
    : `${config.api_url}/ws-get-purchase-history`;

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
    });

    const data = response.data;
    let totalVatAmount = 0;
    let totalAmount = 0;
    let totalCaseQty = 0;
    let grossWeight = 0;
    let cbm = 0;
    let netWeight = 0;

    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Margins and baseline
    const margin = 10;
    let y = margin;

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);
    
    // Helper function to wrap text after specific character count
    const wrapTextByCharacters = (text, maxChars = 15) => {
      if (!text) return [''];
      const lines = [];
      for (let i = 0; i < text.length; i += maxChars) {
        lines.push(text.substring(i, i + maxChars));
      }
      return lines;
    };

    // Helper: format date for display
    const formatDateForDisplay = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return original if invalid
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    };

    // Helper: format date and time for display
    const formatDateTimeForDisplay = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString; // Return original if invalid
        return date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return dateString;
      }
    };

    // Helper: parse date in DD-MM-YYYY format and format for display
    const parseAndFormatDate = (dateString) => {
      if (!dateString) return '';
      try {
        // Handle DD-MM-YYYY format
        if (dateString.includes('-') && dateString.split('-').length === 3) {
          const parts = dateString.split('-');
          if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            // DD-MM-YYYY format
            const date = new Date(parts[2], parts[1] - 1, parts[0]);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              });
            }
          }
        }
        // Try standard date parsing
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        }
        return dateString;
      } catch (e) {
        return dateString;
      }
    };

    // Helper: load logo
    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);
    }

    // Company address
    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom'
    ];
    let smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    // Supplier address (label + first line on same row, remaining lines aligned under value)
    const supplierTitle = 'Supplier Address:';
    const headerSection2 = data.headerSection2 || {};
    const supplierLines = [
      headerSection2.supplier_address_line1 || '',
      headerSection2.supplier_address_line2 || '',
      headerSection2.city || '',
      headerSection2.postcode || '',
      headerSection2.country || ''
    ].filter(Boolean);

    let addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1));
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(supplierTitle, leftX - 25, addrStartY);
    const valueStartX = leftX - 25 + doc.getTextWidth(supplierTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let supplierY = addrStartY;
    let totalSupplierLines = 0;
    if (supplierLines.length > 0) {
      supplierLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, valueStartX, supplierY);
          supplierY += smallLineHeight;
          totalSupplierLines++;
        });
      });
    }

    // Right header
    const rightX = pageWidth - margin;
    // Purchase Order title in big font with tomato color
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71); // Tomato color (RGB: 255, 99, 71)
    doc.text('Purchase Order', rightX, y - 1, { align: 'right' });
    doc.setTextColor(0, 0, 0); // Reset to black color

    // Center-justified company registration info (positioned to the right of company address)
    const regInfoStartX = leftX + 50;
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4 + smallLineHeight * 2);

    const uniqueSoNos = [
      ...new Set((data.products || []).map(p => p.sale_reference).filter(Boolean))
    ];

    const soNoDisplay = uniqueSoNos.join(', ');

    // Create purchase info with proper text wrapping
    const purchaseInfoData = [
      { label: 'Currency', value: headerSection2.currency_id || 'USD' },
      { label: 'PO No', value: headerSection2.reference_no || '' },
      { label: 'Job Ref', value: headerSection2.job_ref || '' },
      { label: 'Arrival Date', value: parseAndFormatDate(headerSection2.arrival_date) || '' },
      { label: 'SO', value: soNoDisplay }
    ];

    // Position supplier info on the right side, below the registration info
    let suppStartY = y + 4 + (smallLineHeight * 4); // Below the registration info
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    const rightSectionMaxWidth = 80; // Maximum width for the right section text
    let currentY = suppStartY;
    
    purchaseInfoData.forEach((item, idx) => {
      if (item.value) {
        // For Job Ref, wrap after 15 characters, for others use normal wrapping
        if (item.label === 'Job Ref') {
          const wrappedValue = wrapTextByCharacters(item.value, 15);
          doc.text(`${item.label}: ${wrappedValue[0]}`, rightX - 40, currentY, { align: 'left' });
          currentY += smallLineHeight;
          
          // Add remaining lines without label
          for (let i = 1; i < wrappedValue.length; i++) {
            doc.text(wrappedValue[i], rightX - 40, currentY, { align: 'left' });
            currentY += smallLineHeight;
          }
        } else {
          const fullText = `${item.label}: ${item.value}`;
          const wrappedLines = wrapText(fullText, rightSectionMaxWidth);
          
          wrappedLines.forEach((line, lineIdx) => {
            doc.text(line, rightX - 40, currentY, { align: 'left' });
            currentY += smallLineHeight;
          });
        }
      }
    });

    const supplierLinesCount = totalSupplierLines;
    y = Math.max(addrStartY + (supplierLinesCount * smallLineHeight), currentY) + 6;

    // Additional supplier info section
    const supplierInfoY = y + 10;
    const sectionWidth = (pageWidth - margin * 2) / 3;

    // VAT / Rep / Account section
    const vatRepAcc = [
      `VAT Reg. No: ${headerSection2.vat_id || ''}`,
      `Rep: ${headerSection2.contact_person_name || ''}`,
      `Account No: ${headerSection2.bank_account_number || ''}`,
    ].filter(Boolean);
    vatRepAcc.forEach((line, idx) => doc.text(line, margin, supplierInfoY + idx * 4));

    // PO / Date / Job Ref section with text wrapping
    const poDateJobData = [
      { label: 'PO No', value: headerSection2.reference_no || '' },
      { label: 'Date of Creation', value: formatDateTimeForDisplay(headerSection2.raised_on) || '' },
      { label: 'Job Ref', value: headerSection2.job_ref || '' }
    ];
    
    let middleSectionY = supplierInfoY;
    const middleSectionMaxWidth = sectionWidth - 5;
    
    poDateJobData.forEach((item) => {
      if (item.value) {
        // For Job Ref, wrap after 15 characters, for others use normal wrapping
        if (item.label === 'Job Ref') {
          const wrappedValue = wrapTextByCharacters(item.value, 15);
          doc.text(`${item.label}: ${wrappedValue[0]}`, margin + sectionWidth, middleSectionY);
          middleSectionY += 4;
          
          // Add remaining lines without label
          for (let i = 1; i < wrappedValue.length; i++) {
            doc.text(wrappedValue[i], margin + sectionWidth, middleSectionY);
            middleSectionY += 4;
          }
        } else {
          const fullText = `${item.label}: ${item.value}`;
          const wrappedLines = wrapText(fullText, middleSectionMaxWidth);
          
          wrappedLines.forEach((line) => {
            doc.text(line, margin + sectionWidth, middleSectionY);
            middleSectionY += 4;
          });
        }
      }
    });

    // Delivery/Collection/Customer Address based on conditions
    let addressSection = [];
    let addressTitle = '';

    if (headerSection2.is_wharehouse_or_customer === 'warehouse' && headerSection2.delivery_type === 'delivery') {
      addressTitle = 'Delivery Address:';
      const warehouseDisplay = headerSection2.warehouse_name
        ? `${headerSection2.warehouse_name} (${headerSection2.job_ref || ''})`
        : `Warehouse (${headerSection2.job_ref || ''})`;
      addressSection = [
        warehouseDisplay,
        headerSection2.address_line1 || '',
        headerSection2.whcity || '',
        headerSection2.whpostcode || '',
        headerSection2.whcountry || ''
      ].filter(Boolean);
    } else if (headerSection2.is_wharehouse_or_customer === 'customer' && headerSection2.delivery_type === 'delivery') {
      addressTitle = 'Customer Address:';
      addressSection = [
        headerSection2.name || '',
        headerSection2.address_line1 || '',
        headerSection2.address_line2 || '',
        headerSection2.whcity || '',
        headerSection2.whstate || '',
        headerSection2.whcountry || '',
        headerSection2.whpostcode || ''
      ].filter(Boolean);
    } else if (headerSection2.delivery_type === 'collection') {
      addressTitle = 'Collection Address:';
      addressSection = [
        headerSection2.address_line1 || '',
        headerSection2.address_line2 || '',
        headerSection2.whcity || '',
        headerSection2.whpostcode || '',
        headerSection2.whcountry || ''
      ].filter(Boolean);
    }

    let deliveryY = supplierInfoY;
    let totalAddressLines = 0;
    if (addressSection.length > 0) {
      doc.setFontSize(8).setFont('helvetica', 'bold');
      doc.text(addressTitle, margin + sectionWidth * 2, deliveryY);
      deliveryY += 4;
      totalAddressLines++;
      
      doc.setFontSize(8).setFont('helvetica', 'normal');
      addressSection.forEach((line) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine) => {
          doc.text(wrappedLine, margin + sectionWidth * 2, deliveryY);
          deliveryY += 4;
          totalAddressLines++;
        });
      });
    }

    // Calculate Y position based on the tallest section
    const vatRepAccLines = vatRepAcc.length;
    const middleSectionLines = Math.ceil((middleSectionY - supplierInfoY) / 4);
    const maxSectionLines = Math.max(vatRepAccLines, middleSectionLines, totalAddressLines);
    y = supplierInfoY + maxSectionLines * 4 + 10;

    // Table
    const columns = [
      'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Unit Price', 'Amount', 'Vat Rate (%)', 'Vat Amount', 'Total Amount'
    ];

    const tableBody = data.products.map((item, index) => {
      const netPrice = item.quantity * item.cost;
      const vatAmount = netPrice * (item.rate / 100);
      const totalPrice = netPrice + vatAmount;

      // Accumulate totals
      totalVatAmount += vatAmount;
      totalAmount += totalPrice;
      totalCaseQty += parseFloat(item.quantity) || 0;
      grossWeight += parseFloat(item.gross_weight) || 0;
      cbm += parseFloat(item.cbm) || 0;
      netWeight += parseFloat(item.net_weight) || 0;

      return [
        index + 1,
        item.code || '',
        wrapText(item.product_desc || '', 50).join('\n'),
        item.pack || '',
        item.size || '',
        item.unit_barcode || '',
        item.quantity || 0,
        item.cost || 0,
        netPrice.toFixed(2),
        item.rate || 0,
        vatAmount.toFixed(2),
        totalPrice.toFixed(2),
      ];
    });

    // Add totals row
    tableBody.push(['', '', 'Total:', '', '', '', totalCaseQty.toFixed(0), '', '', '', totalVatAmount.toFixed(2), totalAmount.toFixed(2)]);

    doc.autoTable({
      startY: y,
      head: [columns],
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000', lineStyle: 'solid' },
      bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 }
    });

    // Footer margin top
    y = doc.autoTable.previous.finalY + 15;

    const addFooterSection = (footerSection) => {
      const adjustedSectionWidth = sectionWidth + 10;
      footerSection.forEach((section, index) => {
        const colStartX = margin + (index % 3) * adjustedSectionWidth;
        let colY = y;
        section.forEach((item) => {
          const wrappedLines = doc.splitTextToSize(item.text, adjustedSectionWidth - 2);
          doc.setFontSize(item.size - 0.5);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          wrappedLines.forEach((line) => {
            doc.text(line, colStartX, colY);
            colY += 4.5;
          });
        });
        if (index % 3 === 2) {
          y = Math.max(y, colY);
        }
      });
      y += 20;
    };

    const addFooterWithSpaceCheck = (footerSection) => {
      const remainingSpace = pageHeight - y - margin;
      const requiredSpace = footerSection.length * 5 + 20;
      if (remainingSpace < requiredSpace) {
        doc.addPage();
        y = margin;
      }
      addFooterSection(footerSection);
    };

    const footerSections1 = [
      [
        { text: `Notes: ${data?.headerSection2?.delivery_notes || ''}`, size: 8, bold: false },
        { text: `Ref: ${data?.headerSection2?.job_ref || ''}`, size: 8, bold: false },
      ],
      [{ text: '', size: 8, bold: false }],
      [
        { text: `Gross Weight: ${grossWeight.toFixed(2)} KGS`, size: 8, bold: false },
        { text: `CBM: ${cbm.toFixed(2)} M3`, size: 8, bold: false },
        { text: `Net Weight: ${netWeight.toFixed(2)} KGS`, size: 8, bold: false },
      ]
    ];

    addFooterWithSpaceCheck(footerSections1);

    // Terms footer
    const footerSections3 = [
      [
        { text: 'Subject to Star Pacific terms and conditions of sale, a copy of which is available on request', size: 8, bold: false },
        { text: 'Star Pacific (No. 7298396)', size: 8, bold: false },
        { text: 'Reservation of Title. The property in and the title to all goods sold by Star Pacific shall not pass to the buyer until all monies due and owing have been paid in full', size: 8, bold: false },
      ]
    ];

    const bottomY = pageHeight - margin - (footerSections3[0].length * 5);
    footerSections3.forEach((section) => {
      const centerX = pageWidth / 2;
      section.forEach((item, i) => {
        wrapText(item.text, pageWidth - 20).forEach((line, lineIdx) => {
          doc.setFontSize(item.size);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
        });
      });
    });

    // Add page numbering
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Page 1/1', pageWidth - margin, pageHeight - margin, { align: 'right' });

    const uniqueFilename = generateUniqueFilename('PurchaseHistoryInvoice', 'pdf');

    if (viewMode) {
      // View mode - open in new tab
      const blobUrl = doc.output('bloburl');
      window.open(blobUrl, '_blank');
    } else {
      // Download mode - trigger actual download
      doc.save(uniqueFilename);
    }

  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

export default generatePurchaseInvoicePDF;